import React, { useEffect, useRef, useState } from 'react';
import CommentEntry from './CommentEntry';
import { commentsRequest, commentScopeKey, commentErrorKey } from '../../utils/comments-api';
import './comments.scss';

const sections = ['invoice', 'source', 'lines', 'removed'];
const uniqueEntries = (entries) => [...new Map(entries.map((entry) => [entry.id, entry])).values()]
  .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '') || a.id.localeCompare(b.id));
const scopeLabel = (scope, t, scopes) => {
  if (scope.section === 'invoice') return t.read('comment_ui.invoice');
  if (scope.section === 'removed') return t.read('comment_ui.removed_line');
  if (scope.section === 'source') {
    const numbers = scopes.filter((item) => item.section === 'lines' && scope.invoice_line_ids?.includes(item.subject.line_id)).map((item) => item.number);
    const label = t.read(scope.subject.line_id ? 'comment_ui.expense_allocation' : 'comment_ui.expense');
    return numbers.length ? `${label} · ${t.read('comment_ui.invoice_lines')} ${numbers.join(', ')}` : label;
  }
  return `${t.read('comment_ui.line')} ${scope.number}${scope.description ? ` · ${scope.description}` : ''}`;
};
const readScope = (scope, cursor) => commentsRequest(`/comments/${scope.subject.type}/${scope.subject.id}`, 'GET', {
  ...scope.query, ...(scope.subject.line_id ? { line_id: scope.subject.line_id } : {}), limit: 50,
  ...(cursor ? { cursor } : {}),
});

const CommentsPanel = ({ invoiceId, revision, data, legacyText = '', onDraftChange }) => {
  const t = data.t;
  const [view, setView] = useState({ invoiceId: null, scopes: [], pages: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [body, setBody] = useState('');
  const [selected, setSelected] = useState('invoice');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [activity, setActivity] = useState(false);
  const generation = useRef(0);
  const requestIdentity = useRef(null);
  const draftListener = useRef(onDraftChange);
  draftListener.current = onDraftChange;
  const current = view.invoiceId === invoiceId;
  const scopes = current ? view.scopes : [];
  const pages = current ? view.pages : {};
  const writable = scopes.filter((scope) => scope.can_add && pages[commentScopeKey(scope)]?.scope?.capabilities?.can_add);
  const hasDestination = writable.some((scope) => (scope.subject.line_id || 'invoice') === selected);

  useEffect(() => { draftListener.current?.(Boolean(body.trim()) || busy); }, [body, busy]);
  useEffect(() => () => draftListener.current?.(false), []);

  const fail = (caught, token) => {
    if (token !== generation.current) return;
    // Clear cached content on any failed authorization/context refresh. The draft
    // is retained locally, but stale destinations cannot be used to post again.
    setView({ invoiceId, scopes: [], pages: {} });
    setConfirm(null);
    setError(commentErrorKey(caught));
  };
  const reload = async () => {
    const token = ++generation.current;
    setLoading(true);
    setError('');
    setView({ invoiceId, scopes: [], pages: {} });
    try {
      const result = await commentsRequest(`/comments/invoice/${invoiceId}/scopes`);
      if (token !== generation.current) return;
      const loaded = await Promise.all(result.scopes.map(async (scope) => [commentScopeKey(scope), await readScope(scope)]));
      if (token !== generation.current) return;
      setView({ invoiceId, scopes: result.scopes, pages: Object.fromEntries(loaded) });
      const available = result.scopes.filter((scope) => scope.can_add);
      if (!body.trim()) setSelected((previous) => available.some((scope) => (scope.subject.line_id || 'invoice') === previous)
        ? previous : available[0]?.subject.line_id || 'invoice');
    } catch (caught) { fail(caught, token); }
    finally { if (token === generation.current) setLoading(false); }
  };
  useEffect(() => {
    // Invoice navigation must not show the previous invoice's bodies or draft.
    setBody('');
    requestIdentity.current = null;
    setBusy(false);
    setConfirm(null);
    reload();
    return () => { generation.current += 1; };
    // reload closes over only the invoice identity; revision refreshes access.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);
  const previousRevision = useRef(revision);
  useEffect(() => {
    if (previousRevision.current !== revision) { previousRevision.current = revision; reload(); }
    // Keep an unsent draft during ordinary invoice saves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);

  const post = async (event) => {
    event.preventDefault();
    const scope = writable.find((item) => (item.subject.line_id || 'invoice') === selected);
    if (!scope || !body.trim() || busy) return;
    const digest = JSON.stringify([invoiceId, selected, body]);
    if (requestIdentity.current?.digest !== digest) {
      requestIdentity.current = { digest, key: window.crypto.randomUUID() };
    }
    const token = generation.current;
    setBusy(true);
    setError('');
    try {
      const entry = await commentsRequest(`/comments/invoice/${invoiceId}`, 'POST', {
        body, client_request_id: requestIdentity.current.key, ...(scope.subject.line_id ? { line_id: scope.subject.line_id } : {}),
      });
      if (token !== generation.current) return;
      const key = commentScopeKey(scope);
      setView((previous) => ({ ...previous, pages: { ...previous.pages,
        [key]: { ...previous.pages[key], entries: uniqueEntries([...previous.pages[key].entries, entry]) } } }));
      setBody('');
      setActivity(false);
      requestIdentity.current = null;
    } catch (caught) {
      if (token !== generation.current) return;
      if (![403, 404, 409].includes(caught?.response?.status)) setError(commentErrorKey(caught));
      else fail(caught, token);
    } finally { if (token === generation.current) setBusy(false); }
  };
  const loadMore = async (scope) => {
    const token = generation.current;
    const key = commentScopeKey(scope);
    setBusy(true);
    try {
      const page = await readScope(scope, pages[key].next_cursor);
      if (token !== generation.current) return;
      setView((previous) => ({ ...previous, pages: { ...previous.pages,
        [key]: { ...page, entries: uniqueEntries([...previous.pages[key].entries, ...page.entries]) } } }));
    } catch (caught) { fail(caught, token); }
    finally { if (token === generation.current) setBusy(false); }
  };
  const remove = async () => {
    if (!confirm || busy) return;
    const token = generation.current;
    setBusy(true);
    try {
      const deleted = await commentsRequest(`/comments/${confirm.entry.id}`, 'DELETE', { expected_version: confirm.entry.version });
      if (token !== generation.current) return;
      setView((previous) => ({ ...previous, pages: Object.fromEntries(Object.entries(previous.pages).map(([key, page]) =>
        [key, { ...page, entries: page.entries.map((entry) => entry.id === deleted.id ? deleted : entry) }])) }));
      setConfirm(null);
    } catch (caught) { fail(caught, token); }
    finally { if (token === generation.current) setBusy(false); }
  };
  const loadedCount = Object.values(pages).reduce((count, page) => count + page.entries.length, 0);
  return (
    <div className="xpens-comments">
      <div className="comments-heading"><h4>{t.read('comments')}</h4><small>{loadedCount} {t.read('comment_ui.loaded')}</small>
        <small>{t.read('comment_ui.oldest_first')}</small></div>
      <div className="comments-tabs"><button type="button" aria-pressed={!activity} onClick={() => setActivity(false)}>{t.read('comments')}</button>
        <button type="button" aria-pressed={activity} onClick={() => setActivity(true)}>{t.read('comment_ui.activity')}</button></div>
      {loading && <p role="status">{t.read('comment_ui.loading')}</p>}
      {error && <div role="alert"><p>{t.read(error)}</p><button type="button" className="comment-action" disabled={busy || loading} onClick={reload}>{t.read('comment_ui.refresh')}</button></div>}
      <form className="comment-composer" onSubmit={post}>
        <span className="comment-avatar" aria-hidden="true">{[data.firstName, data.lastName].filter(Boolean).map((name) => name[0]).join('')}</span>
        <div className="comment-content"><textarea aria-label={t.read('comment_ui.add_label')} placeholder={t.read('comment_ui.placeholder')}
          maxLength={2000} value={body} disabled={busy || loading} onChange={(event) => setBody(event.target.value)} />
          <div className="comment-post-controls">
            {writable.length > 0 && <label>{t.read('comment_ui.destination')} <select aria-label={t.read('comment_ui.destination')}
              value={selected} disabled={busy} onChange={(event) => setSelected(event.target.value)}>
              {!hasDestination && <option value={selected} disabled>{t.read('comment_ui.destination_unavailable')}</option>}
              {writable.map((scope) => <option key={commentScopeKey(scope)} value={scope.subject.line_id || 'invoice'}>{scopeLabel(scope, t, scopes)}</option>)}
            </select></label>}
            <small>{t.read(writable.length ? 'comment_ui.visibility' : 'comment_ui.read_only')}</small>
            <button className="btn btn-primary" type="submit" disabled={!hasDestination || !body.trim() || busy || loading}>{t.read('comment_ui.add')}</button>
          </div>
        </div>
      </form>
      {confirm && <div className="comment-delete-confirm" role="alertdialog" aria-label={t.read('comment_ui.confirm_delete')}>
        <p>{t.read('comment_ui.delete_question')}</p><button type="button" className="btn btn-danger" disabled={busy} onClick={remove}>{t.read('comment_ui.confirm_delete')}</button>
        <button type="button" className="comment-action" disabled={busy} onClick={() => setConfirm(null)}>{t.read('cancel')}</button>
      </div>}
      {sections.map((section) => {
        const groups = scopes.filter((scope) => scope.section === section);
        if (!groups.length) return null;
        return <section key={section} aria-label={t.read(`comment_ui.${section}_heading`)} className="comment-section">
          <h5>{t.read(`comment_ui.${section}_heading`)}</h5>
          {section === 'source' && <small>{t.read('comment_ui.source_note')}</small>}
          {groups.map((scope) => {
            const key = commentScopeKey(scope);
            const page = pages[key];
            const entries = (page?.entries || []).filter((entry) => activity ? entry.kind === 'system_note' : entry.kind !== 'system_note');
            return <div key={key} className="comment-discussion">
              {section !== 'invoice' && <div className="comment-scope-label">{scopeLabel(scope, t, scopes)}</div>}
              {entries.map((entry) => <CommentEntry key={entry.id} entry={entry} data={data} readOnly={section === 'source'} deleting={busy}
                onDelete={(target) => setConfirm({ entry: target })} />)}
              {!entries.length && <p className="comment-empty">{t.read('comment_ui.empty')}</p>}
              {page?.next_cursor && <button type="button" className="comment-action" disabled={busy} onClick={() => loadMore(scope)}>{t.read('comment_ui.load_more')}</button>}
            </div>;
          })}
          {section === 'invoice' && legacyText && <div className="comment-legacy"><strong>{t.read('comment_ui.previous')}</strong><p>{legacyText}</p></div>}
        </section>;
      })}
    </div>
  );
};
export default CommentsPanel;
